import { useCallback, type Dispatch, type SetStateAction } from "react";
import {
  removeRoundFromSets,
  seedForIntensityKind,
  VALUELESS_KINDS,
  type BlockMember,
  type BlockSetRow,
  type IntensityKind,
} from "@jackedlogs/shared";

import { blankBlockMember, blankBlockRow } from "./composeBlocks";
import type { ComposerForm } from "./useComposer";

// The composer's form-CONTENT mutations: what is in the block being composed
// (members, their rows, which days/weeks it lands on). Split out of useComposer
// for size (SP-USECOMPOSER-FORM), the same 500-line budget that already carved
// out useRampEdit and useComposerCommit.
//
// ⛔ The STRUCTURAL edits — cloneDay / cloneWeek / addDay / removeDay /
// changeCycleWeeks — deliberately did NOT move here. They write the DRAFT
// (sessions, cycleWeeks) rather than the form, and they are the scheduler
// surface, which is locked. As written this hook is handed only setForm and a
// slug->name lookup, so it cannot reach the draft — but that is the shape of
// the code today, not something the compiler enforces: adding setDraft to the
// params below would meet no resistance. Don't.
export function useComposerFormMutations({
  setForm,
  nameOf,
}: {
  setForm: Dispatch<SetStateAction<ComposerForm>>;
  nameOf: (slug: string) => string | undefined;
}) {
  // --- picker commits -----------------------------------------------------
  // `initialDays` seeds the placement when adding to an EMPTY form: the
  // edit-in-place program composer adds to the day you're looking at, so a
  // fresh exercise lands on that day only (not all days — the per-day list
  // would otherwise sprout the exercise everywhere at once). Single mode
  // never passes it (one day) → the allOf default is unchanged.
  const addExercises = useCallback(
    (slugs: string[], initialDays?: Set<number>) => {
      setForm((f) => ({
        ...f,
        days: f.members.length === 0 && initialDays ? initialDays : f.days,
        members: [
          ...f.members,
          ...slugs.map((s) => blankBlockMember(s, nameOf(s))),
        ],
      }));
    },
    [nameOf, setForm],
  );

  // The picker's Superset tab: the picked members become ONE grouped block.
  const addSuperset = useCallback(
    (slugs: string[], initialDays?: Set<number>) => {
      setForm((f) => ({
        ...f,
        grouped: true,
        days: f.members.length === 0 && initialDays ? initialDays : f.days,
        members: [
          ...f.members,
          ...slugs.map((s) => blankBlockMember(s, nameOf(s))),
        ],
      }));
    },
    [nameOf, setForm],
  );

  // --- form mutations -----------------------------------------------------
  const patchMember = useCallback(
    (idx: number, fn: (m: BlockMember) => BlockMember) => {
      setForm((f) => ({
        ...f,
        members: f.members.map((m, i) => (i === idx ? fn(m) : m)),
      }));
    },
    [setForm],
  );

  const removeMember = useCallback(
    (idx: number) => {
      setForm((f) => {
        const members = f.members.filter((_, i) => i !== idx);
        return { ...f, members, grouped: f.grouped && members.length > 1 };
      });
    },
    [setForm],
  );

  // ⇄ swap the member's exercise, keep its prescription (rows, flags,
  // notes) — the clone→retarget gesture: clone bench D1, swap to squat,
  // re-day to D2, done.
  const replaceMember = useCallback(
    (idx: number, slug: string) => {
      patchMember(idx, (m) => ({
        ...m,
        exerciseId: slug,
        exerciseName: nameOf(slug),
      }));
    },
    [patchMember, nameOf],
  );

  const updateRow = useCallback(
    (memberIdx: number, rowIdx: number, patch: Partial<BlockSetRow>) => {
      patchMember(memberIdx, (m) => ({
        ...m,
        setRows: m.setRows.map((r, i) => (i === rowIdx ? { ...r, ...patch } : r)),
      }));
    },
    [patchMember],
  );

  // Per-EXERCISE note = slot instructions (Antenore 2026-06-12): what
  // programResolution surfaces in the logger as the entry's 💬 note.
  const setMemberInstructions = useCallback(
    (memberIdx: number, text: string) => {
      patchMember(memberIdx, (m) => ({ ...m, instructions: text }));
    },
    [patchMember],
  );

  // The header IS the kind selector (§5.2): tapping it re-kinds every row.
  const setMemberKind = useCallback(
    (memberIdx: number, kind: IntensityKind) => {
      const valueless = VALUELESS_KINDS.has(kind);
      patchMember(memberIdx, (m) => ({
        ...m,
        setRows: m.setRows.map((r) => ({
          ...r,
          intensityKind: kind,
          intensityValue: valueless
            ? ""
            : r.intensityValue || seedForIntensityKind(kind),
        })),
      }));
    },
    [patchMember],
  );

  // Logger idiom (add_round_to_group): ONE action appends a set to EVERY
  // member, each seeded from its own last row — rounds stay aligned
  // (1A·1B·2A·2B). A solo block is just a group of one, same path.
  const addRound = useCallback(() => {
    setForm((f) => ({
      ...f,
      members: f.members.map((m) => {
        const last = m.setRows[m.setRows.length - 1];
        return {
          ...m,
          setRows: [...m.setRows, last ? { ...last, note: "" } : blankBlockRow()],
        };
      }),
    }));
  }, [setForm]);

  // Logger idiom (remove_round_from_group): drop the whole round across
  // members; the per-member safety rules live in shared removeRoundFromSets,
  // the same implementation the logger reducer runs.
  const removeRound = useCallback(
    (roundIdx: number) => {
      setForm((f) => ({
        ...f,
        members: f.members.map((m) => {
          const setRows = removeRoundFromSets(m.setRows, roundIdx);
          return setRows === m.setRows ? m : { ...m, setRows };
        }),
      }));
    },
    [setForm],
  );

  const toggleDay = useCallback(
    (d: number) => {
      setForm((f) => {
        const days = new Set(f.days);
        if (days.has(d)) days.delete(d);
        else days.add(d);
        return { ...f, days };
      });
    },
    [setForm],
  );

  const toggleWeek = useCallback(
    (w: number) => {
      setForm((f) => {
        const weeks = new Set(f.weeks);
        if (weeks.has(w)) weeks.delete(w);
        else weeks.add(w);
        return { ...f, weeks };
      });
    },
    [setForm],
  );

  return {
    addExercises,
    addSuperset,
    removeMember,
    replaceMember,
    updateRow,
    setMemberInstructions,
    setMemberKind,
    addRound,
    removeRound,
    toggleDay,
    toggleWeek,
  };
}
